import { Title, Meta } from "@angular/platform-browser";
import { Injectable, Inject } from '@angular/core';
import { DOCUMENT } from '@angular/common';
import { Router, NavigationEnd } from '@angular/router';
import { filter } from 'rxjs/operators';

@Injectable({ providedIn: 'root' })
export class SeoService {
  private readonly DEFAULT_TITLE = 'Innovation Smart Plaza - Bandi e Innovazione';
  private readonly DEFAULT_DESCRIPTION = 'Accedi a bandi sempre aggiornati e scopri le opportunità più rilevanti per i tuoi obiettivi di innovazione.';
  private readonly DEFAULT_IMAGE = 'https://www.innovationplaza.it/assets/logoConBackground.png';

  constructor(
    private titleService: Title,
    private meta: Meta,
    private router: Router,
    @Inject(DOCUMENT) private document: Document
  ) {
    // Il blocco di aggiornamento automatico è stato rimosso per evitare reset ai valori di default durante la navigazione
  }

  updateTitle(title?: string) {
    const finalTitle = title || this.DEFAULT_TITLE;
    this.titleService.setTitle(finalTitle);
    this.meta.updateTag({ property: 'og:title', content: finalTitle });
  }

  updateDescription(desc?: string) {
    const finalDesc = desc || this.DEFAULT_DESCRIPTION;
    this.meta.updateTag({ name: 'description', content: finalDesc });
    this.meta.updateTag({ property: 'og:description', content: finalDesc });
  }

  updateCanonicalLink(url?: string) {
    const finalUrl = url || this.document.URL;
    let link: HTMLLinkElement | null = this.document.querySelector('link[rel="canonical"]');
    if (link) {
      link.setAttribute('href', finalUrl);
    } else {
      link = this.document.createElement('link');
      link.setAttribute('rel', 'canonical');
      link.setAttribute('href', finalUrl);
      this.document.head.appendChild(link);
    }
  }

  updateOgUrl(url?: string) {
    const finalUrl = url || this.document.URL;
    this.meta.updateTag({ property: 'og:url', content: finalUrl });
  }

  updateOgImage(image?: string) {
    const finalImage = image || this.DEFAULT_IMAGE;
    this.meta.updateTag({ property: 'og:image', content: finalImage });
  }

  updateOgType(type: string = 'website') {
    this.meta.updateTag({ property: 'og:type', content: type });
  }

  setJsonLd(data: any) {
    let script = this.document.querySelector('script[type="application/ld+json"]');
    if (script) {
      script.textContent = JSON.stringify(data);
    } else {
      script = this.document.createElement('script');
      script.setAttribute('type', 'application/ld+json');
      script.textContent = JSON.stringify(data);
      this.document.head.appendChild(script);
    }
  }

  updateSeo(data: any = {}) {
    const title = data.titleSeo || this.DEFAULT_TITLE;
    const description = data.metadescription || this.DEFAULT_DESCRIPTION;
    const url = data.url || this.document.URL;
    const image = data.image || this.DEFAULT_IMAGE;

    // Dynamic logic for types based on URL
    let jsonLdType = 'WebSite';
    let ogType = 'website';

    if (url.includes('/cerca-bando')) {
      jsonLdType = 'GovernmentService';
      ogType = 'article';
    }

    this.updateTitle(title);
    this.updateDescription(description);
    this.updateOgUrl(url);
    this.updateCanonicalLink(url);
    this.updateOgImage(image);
    this.updateOgType(data.type || ogType);

    // Generate JSON-LD automatically if not provided, or use provided one
    if (data.jsonLd) {
      this.setJsonLd(data.jsonLd);
    } else {
      const structuredData = {
        "@context": "https://schema.org",
        "@type": jsonLdType,
        "name": title,
        "description": description,
        "url": url,
        "image": image
      };
      this.setJsonLd(structuredData);
    }
  }


}
